import { useState, useRef, useCallback, useEffect } from 'react'
import { speechLocaleOf } from './locale'

// Speech-to-text as a hook. Continuous + interim results, so a learner can
// answer in a few sentences; `onFinal(text)` fires with each finalised chunk,
// `interim` shows what is being heard right now.
//
//   const stt = useSpeechToText('tur', { onFinal: (t) => append(t) })
//   stt.supported · stt.listening · stt.interim · stt.start() · stt.stop()
export default function useSpeechToText(langCode, { onFinal } = {}) {
  const [supported] = useState(() => typeof window !== 'undefined' && !!(window.SpeechRecognition || window.webkitSpeechRecognition))
  const [listening, setListening] = useState(false)
  const [interim, setInterim] = useState('')
  const [error, setError] = useState('')
  const recRef = useRef(null)
  const onFinalRef = useRef(onFinal)
  useEffect(() => { onFinalRef.current = onFinal }, [onFinal])

  const stop = useCallback(() => {
    const r = recRef.current
    if (!r) return
    try { r.stop() } catch { /* already stopped */ }
  }, [])

  const start = useCallback(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition
    if (!SR) return
    stop()
    const rec = new SR()
    rec.lang = speechLocaleOf(langCode)
    rec.interimResults = true
    rec.continuous = true
    rec.maxAlternatives = 1
    rec.onstart = () => { setListening(true); setInterim(''); setError('') }
    rec.onresult = (e) => {
      let fin = ''
      let tmp = ''
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const t = e.results[i][0].transcript
        if (e.results[i].isFinal) fin += t
        else tmp += t
      }
      setInterim(tmp)
      if (fin.trim() && onFinalRef.current) onFinalRef.current(fin.trim())
    }
    rec.onerror = (e) => { setError(e && e.error === 'not-allowed' ? 'Microphone access was blocked.' : (e && e.error) || 'Speech recognition error'); setListening(false) }
    rec.onend = () => { setListening(false); setInterim('') }
    recRef.current = rec
    try { rec.start() } catch { setListening(false) }
  }, [langCode, stop])

  useEffect(() => () => stop(), [stop])
  return { supported, listening, interim, error, start, stop }
}
